import * as React from "react";
import { Theme } from '@mui/material/styles';
import { createStyles } from '@mui/styles';
import { withStyles, WithStyles } from '@mui/styles';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { connect } from "react-redux";
import { getAppInsights } from "../../Logging/TelemetryService";
import { Alert, AlertTitle, Typography, Box } from '@mui/material';

interface IErrorBoundryStateProps {
    hasError: boolean;
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

const mapStateToProps = (): IErrorBoundryStateProps => ({
    hasError: false
});

const styles = (theme: Theme) => createStyles({
    flex: {
        flexGrow: 1,
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justify: 'center',
        background: 'white',
        padding: theme.spacing(3),
        minHeight: '200px',
    },
    errorContainer: {
        width: '100%',
        maxWidth: '600px',
        margin: 'auto',
    }
});

type IStylesProps = WithStyles<typeof styles> & {
    children?: React.ReactNode;
};

// VIEW
class ErrorBoundaryView extends React.Component<IStylesProps, IErrorBoundryStateProps> {
    constructor(props: IStylesProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): IErrorBoundryStateProps {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Display fallback UI
        this.setState({ hasError: true, error, errorInfo });

        // Safely track the error with Application Insights
        try {
            const appInsights = getAppInsights();
            if (appInsights && appInsights.trackException) {
                appInsights.trackException({ 
                    error: error, 
                    severityLevel: SeverityLevel.Error,
                    properties: {
                        componentStack: errorInfo.componentStack,
                        errorBoundary: true,
                        errorMessage: error.message,
                        errorStack: error.stack
                    }
                });
            }
        } catch (telemetryError) {
            // Don't let telemetry errors break the error boundary
            console.warn('Failed to track error with Application Insights:', telemetryError);
        }

        // Log error to console for development
        console.error('Error caught by Error Boundary:', error);
        console.error('Error Info:', errorInfo);
    }

    render() {
        const { classes } = this.props;
        
        if (this.state.hasError) {
            return (
                <Box className={classes.root}>
                    <Box className={classes.errorContainer}>
                        <Alert severity="error">
                            <AlertTitle>Something went wrong</AlertTitle>
                            <Typography variant="body2">
                                An unexpected error occurred. Please try refreshing the page.
                            </Typography>
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <Box mt={2}>
                                    <Typography variant="caption" component="pre" style={{ whiteSpace: 'pre-wrap' }}>
                                        {this.state.error.message}
                                    </Typography>
                                </Box>
                            )}
                        </Alert>
                    </Box>
                </Box>
            );
        }
        
        return this.props.children;
    }

    componentWillUnmount() {
        this.setState({ hasError: false });
    }
}

// REDUX CONTAINER
const ErrorBoundaryViewState = connect<IErrorBoundryStateProps>(mapStateToProps)(ErrorBoundaryView);

// STYLES CONTAINER
const ErrorBoundary = withStyles(styles)(ErrorBoundaryViewState);

export default ErrorBoundary;
